import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Discount, CurrencyObject, Shopper } from 'common/types';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import { Theme } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { getPricingString } from 'common/utils/common';
import { useTranslation } from 'services/localization/useTranslation';
import { usePermissions } from 'hooks/usePermissions';

interface DiscountProps {
	open: boolean;
	onClose: (discount: Discount) => void;
	discount: Discount;
	originalPrice: number;
	currency: CurrencyObject;
	shopperSpecific?: Shopper;
}

type Props = DiscountProps & WithStyles<typeof styles>;

const discountOptions = [
	0,
	0.05,
	0.1,
	0.15,
	0.2,
	0.25,
	0.3,
	0.35,
	0.4,
	0.45,
	0.5,
	0.55,
	0.6,
	0.65,
	0.7,
	0.75,
	0.8,
	0.85,
	0.9,
	0.95,
	1.0,
];

const DiscountDialog = (props: Props) => {
	const [discount, setDiscount] = useState<Discount>(props.discount);
	const [discountError, setDiscountError] = useState<string>('');
	const { t } = useTranslation();
	const { hasPermission } = usePermissions();

	const originalPrice = props.originalPrice;
	const discountedPrice =
		discount.amount || discount.amount === 0 ? originalPrice - (discount.amount || 0) : '';
	const actualDiscountPercentage = !originalPrice ? 0 : (discount.amount || 0) / originalPrice;
	useEffect(() => {
		if (props.discount.percentage && actualDiscountPercentage !== props.discount.percentage) {
			setDiscount({
				amount: props.discount.amount,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const error =
			discount.amount < 0
				? t('discountDialog.invalidHighPrice', 'Price cannot be larger than the original price.')
				: discount.amount > originalPrice
				? t('discountDialog.invalidNegativePrice', 'Price cannot be negative')
				: '';
		if (error !== discountError) {
			setDiscountError(error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [discount.amount]);

	const handleCancel = () => props.onClose(props.discount);

	const handleOk = () => props.onClose(discount);

	const handlePercentageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const parsedValue: number = parseFloat(event.target.value);
		const percentage: number | '' = !isNaN(parsedValue) ? parsedValue : '';
		const discountAmount: number = !isNaN(parsedValue)
			? Math.round(originalPrice * parsedValue)
			: 0;
		setDiscount({
			percentage,
			amount: discountAmount,
		});
	};

	const handleDiscountedAmountChange = (
		event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>,
	) => {
		const parsedValue: number = Math.round(parseFloat(event.target.value) * 100);
		const discountAmount: number | '' = !isNaN(parsedValue)
			? originalPrice - (parsedValue || 0)
			: '';
		setDiscount({
			amount: discountAmount,
		});
	};

	const getDialogTitle = () =>
		props.shopperSpecific
			? t('discountDialog.giveDiscountShopper', {
					firstName: props.shopperSpecific.firstName,
					defaultValue: 'Give discount for person: {{firstName}}',
			  })
			: t('discountDialog.giveDiscount', 'Give discount');

	return (
		<Dialog
			disableEscapeKeyDown
			maxWidth="xs"
			aria-labelledby="discount-dialog-title"
			open={props.open}
			classes={{
				paper: props.classes.discountDialogPaper,
			}}
		>
			<DialogTitle id="discount-dialog-title">{getDialogTitle()}</DialogTitle>
			<DialogContent>
				<Typography variant="body1" gutterBottom>
					{t('discountDialog.originalPrice', 'Original price')}:{' '}
					{getPricingString(originalPrice, props.currency)}
				</Typography>
				<Typography variant="body1" gutterBottom>
					{t('discountDialog.discountAmount', 'Discount amount')}:{' '}
					{getPricingString(discount.amount || 0, props.currency)}
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextField
							select
							variant="standard"
							label={t('discountDialog.discountPercentage', 'Discount percentage')}
							name="discount-percentage"
							value={discount.percentage || discount.percentage === 0 ? discount.percentage : ''}
							onChange={handlePercentageChange}
							SelectProps={{
								native: true,
							}}
							margin="normal"
							type="number"
							fullWidth
						>
							<option value={''} />
							{discountOptions.map((option) => (
								<option key={option} value={option}>
									{(option || option === 0) && typeof option === 'number'
										? Math.round(option * 100) + ' %'
										: ''}
								</option>
							))}
						</TextField>
					</Grid>
					<Grid item xs={12}>
						<TextField
							variant="standard"
							label={
								props.shopperSpecific
									? t('discountDialog.finalPriceShopperSpecific', 'Final price for person')
									: t('discountDialog.finalPrice', 'Final price of rental')
							}
							value={discountedPrice || discountedPrice === 0 ? discountedPrice / 100 : ''}
							onChange={handleDiscountedAmountChange}
							type="number"
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">{props.currency.displayAs}</InputAdornment>
								),
							}}
							inputProps={{
								step: 0.01,
							}}
							fullWidth
						/>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<Typography variant="body2" color="error">
						{discountError}
					</Typography>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCancel} color="secondary">
					{t('shop.cancel')}
				</Button>
				<Button
					onClick={handleOk}
					color="primary"
					variant="contained"
					disabled={!!discountError || !hasPermission('edit_booking')}
				>
					{t('discountDialog.applyDiscount', 'Apply discount')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		discountDialogPaper: {
			width: '80%',
			maxHeight: 435,
		},
	});

export default withStyles(styles)(DiscountDialog);
